/**
 * Importar datos desde Excel — `OD-0057` (`ACCEPTED` 2026-10-24), en
 * Configuración.
 *
 * ```text
 * 1 ELEGIR     que se importa. Los tipos los da la API, ya filtrados por el
 *              plan y el rol: la web no compara planes (URS §105)
 * 2 SUBIR      un .xlsx o un .csv. Se LEE AQUI: el fichero no sale del
 *              ordenador, solo sus filas como texto (D-07)
 * 3 EMPAREJAR  solo la avanzada. La minima exige las columnas de la plantilla
 * 4 REVISAR    la API comprueba las filas SIN escribir: cuantas estan bien,
 *              cuantas ya existian y cuales tienen errores, con el motivo
 * 5 IMPORTAR   en trozos, con una clave por fichero: si se corta, repetir no
 *              duplica nada
 * ```
 *
 * Las facturas de otro programa piden, antes de importar, que se lea y se
 * acepte el aviso de D-08: son una copia de consulta.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  apiImportacion,
  type ColumnaImportacion,
  type Importacion,
  type LimitesImportacion,
  type ResultadoFila,
  type TipoImportacion,
} from "../api/cliente";
import { t } from "../i18n/es";
import { csvDeErrores, emparejar, faltan, filaDelError, itemsDe, trozos, type Emparejamiento, type GrupoDeFilas } from "../ui/importacion";
import { leerHoja, type Hoja } from "../ui/lector-hoja";
import { Aviso, Boton, Casilla, Selector } from "../ui/primitivas";

const NO = "__no__";
const MAX_ERRORES_EN_PANTALLA = 20;

type Fichero = { readonly nombre: string; readonly bytes: number; readonly hoja: Hoja; readonly clave: string };

type Resultados = ReadonlyMap<number, ResultadoFila>;
type Motivos = Map<number, string>;

type Paso =
  | { readonly tipo: "elegir" }
  | { readonly tipo: "emparejar"; readonly fichero: Fichero; readonly emparejamiento: Emparejamiento }
  | {
      readonly tipo: "revisar";
      readonly fichero: Fichero;
      readonly grupos: readonly GrupoDeFilas[];
      readonly resultados: Resultados;
      readonly libres: number | null;
    }
  | { readonly tipo: "hecho"; readonly fichero: Fichero; readonly importacion: Importacion; readonly motivos: Motivos };

type PasoRevisar = Extract<Paso, { tipo: "revisar" }>;

function guardar(contenido: Blob, nombre: string): void {
  const url = URL.createObjectURL(contenido);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** «2026-11-01» -> «01/11/2026». */
function dia(iso: string): string {
  return iso.split("-").reverse().join("/");
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
}

/** «Fila 14 — NIF: falta; Total: no cuadra…», en el idioma de quien lo lee. */
function motivoDe(r: ResultadoFila): string {
  const partes = Object.entries(r.campos).map(([campo, codigo]) => {
    const clave = campo.split(".").pop() ?? campo;
    return t.importar.motivo(t.importar.campos[clave] ?? clave, t.importar.errores[codigo] ?? t.importar.errorGenerico);
  });
  return partes.join("; ");
}

/** Los motivos por FILA del Excel. En una factura, el de una línea va a su fila. */
function motivosPorFila(grupos: readonly GrupoDeFilas[], resultados: Resultados): Motivos {
  const out: Motivos = new Map();
  for (const g of grupos) {
    const r = resultados.get(g.item.row);
    if (r === undefined || r.resultado !== "REJECTED") continue;
    for (const [campo, codigo] of Object.entries(r.campos)) {
      const fila = filaDelError(g, campo);
      const texto = motivoDe({ ...r, campos: { [campo]: codigo } });
      out.set(fila, out.has(fila) ? `${out.get(fila)}; ${texto}` : texto);
    }
  }
  return out;
}

function descargarErrores(fichero: Fichero, motivos: Motivos): void {
  const csv = csvDeErrores(fichero.hoja, motivos, t.importar.columnaMotivo);
  guardar(new Blob([csv], { type: "text/csv;charset=utf-8" }), `errores-${fichero.nombre.replace(/\.[^.]+$/, "")}.csv`);
}

export function PantallaImportar({ hayConexion }: { readonly hayConexion: boolean }) {
  const [limites, setLimites] = useState<LimitesImportacion | null>(null);
  const [historial, setHistorial] = useState<readonly Importacion[]>([]);
  const [tipo, setTipo] = useState<TipoImportacion>("CUSTOMERS");
  const [paso, setPaso] = useState<Paso>({ tipo: "elegir" });
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [entendido, setEntendido] = useState(false);
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const entrada = useRef<HTMLInputElement>(null);

  const cargar = useCallback(async () => {
    const r = await apiImportacion.estado();
    if (!r.ok) {
      setError(r.error?.message ?? t.importar.error);
      return;
    }
    setLimites(r.datos.limites);
    setHistorial(r.datos.importaciones);
    setTipo((actual) => (r.datos.limites.tipos.includes(actual) ? actual : (r.datos.limites.tipos[0] ?? actual)));
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (limites === null) return error === null ? <p className="ayuda-neutra">{t.importar.leyendo}</p> : <Aviso tono="error">{error}</Aviso>;

  const minima = limites.nivel === "MINIMAL";
  const columnas: readonly ColumnaImportacion[] = limites.columnas[tipo] ?? [];
  const sinCupo = limites.usadasEsteMes >= limites.alMes;
  const nombreCampo = (key: string): string => columnas.find((c) => c.key === key)?.header ?? key;

  const empezarDeNuevo = (): void => {
    setPaso({ tipo: "elegir" });
    setError(null);
    setEntendido(false);
    if (entrada.current !== null) entrada.current.value = "";
  };

  const comprobar = async (fichero: Fichero, emparejamiento: Emparejamiento): Promise<void> => {
    const grupos = itemsDe(fichero.hoja, emparejamiento, columnas, tipo === "EXTERNAL_INVOICES");
    const resultados = new Map<number, ResultadoFila>();
    let libres: number | null = null;
    let hechas = 0;
    for (const trozo of trozos(grupos.map((g) => g.item), limites.filasPorPeticion)) {
      setOcupado(t.importar.comprobando(hechas, grupos.length));
      const r = await apiImportacion.comprobar(tipo, trozo);
      if (!r.ok) {
        setOcupado(null);
        setError(r.error === null ? t.importar.error : [r.error.message, r.error.action].filter((x) => x !== null).join(" "));
        return;
      }
      for (const x of r.datos.resultados) resultados.set(x.fila, x);
      libres = r.datos.libres;
      hechas += trozo.length;
    }
    setOcupado(null);
    setPaso({ tipo: "revisar", fichero, grupos, resultados, libres });
  };

  const alElegirFichero = async (f: File): Promise<void> => {
    setError(null);
    if (f.size > limites.maxBytes) {
      setError(t.importar.demasiadoGrande(Math.round(limites.maxBytes / 1024 / 1024)));
      return;
    }
    setOcupado(t.importar.leyendo);
    const hoja = await leerHoja(f.name, new Uint8Array(await f.arrayBuffer()));
    setOcupado(null);
    if (typeof hoja === "string") {
      setError(t.importar.errorLectura[hoja]);
      return;
    }
    if (hoja.filas.length > limites.maxFilas) {
      setError(t.importar.demasiadasFilas(hoja.filas.length, limites.maxFilas));
      return;
    }
    const fichero: Fichero = { nombre: f.name, bytes: f.size, hoja, clave: `imp-${crypto.randomUUID()}` };
    const emparejamiento = emparejar(hoja.cabeceras, columnas, minima);
    if (minima) {
      // La mínima trabaja con la plantilla: tienen que estar todas sus columnas obligatorias.
      const sin = faltan(emparejamiento, columnas);
      if (sin.length > 0) {
        setError(t.importar.faltanEnPlantilla(sin.map(nombreCampo).join(", ")));
        return;
      }
      await comprobar(fichero, emparejamiento);
      return;
    }
    setPaso({ tipo: "emparejar", fichero, emparejamiento });
  };

  const importar = async (p: PasoRevisar): Promise<void> => {
    setError(null);
    const enviar = p.grupos.filter((g) => p.resultados.get(g.item.row)?.resultado !== "REJECTED");
    setOcupado(t.importar.importando(0, enviar.length));
    const inicio = await apiImportacion.empezar(
      { kind: tipo, fileName: p.fichero.nombre, totalRows: p.fichero.hoja.filas.length, fileBytes: p.fichero.bytes },
      p.fichero.clave,
    );
    if (!inicio.ok) {
      setOcupado(null);
      setError(inicio.error === null ? t.importar.error : [inicio.error.message, inicio.error.action].filter((x) => x !== null).join(" "));
      return;
    }
    const resultados = new Map(p.resultados);
    let hechas = 0;
    for (const trozo of trozos(enviar.map((g) => g.item), limites.filasPorPeticion)) {
      const r = await apiImportacion.enviarFilas(inicio.datos.id, trozo);
      if (!r.ok) {
        setOcupado(null);
        setError(t.importar.errorAMedias(hechas));
        return;
      }
      for (const x of r.datos) resultados.set(x.fila, x);
      hechas += trozo.length;
      setOcupado(t.importar.importando(hechas, enviar.length));
    }
    const todos = [...resultados.values()];
    const fin = await apiImportacion.terminar(inicio.datos.id, {
      duplicates: todos.filter((x) => x.resultado === "DUPLICATE").length,
      rejected: todos.filter((x) => x.resultado === "REJECTED").length,
    });
    setOcupado(null);
    if (!fin.ok) {
      setError(t.importar.error);
      return;
    }
    setPaso({ tipo: "hecho", fichero: p.fichero, importacion: fin.datos, motivos: motivosPorFila(p.grupos, resultados) });
    void cargar();
  };

  const deshacer = async (id: string): Promise<void> => {
    setConfirmando(null);
    setOcupado(t.importar.deshaciendo);
    const r = await apiImportacion.deshacer(id);
    setOcupado(null);
    if (!r.ok) {
      setError(r.error?.message ?? t.importar.error);
      return;
    }
    setAviso(t.importar.deshecha(r.datos.quitadas, r.datos.seQuedan));
    void cargar();
  };

  const plantilla = async (): Promise<void> => {
    const r = await apiImportacion.descargarPlantilla(tipo);
    if (r.ok) guardar(r.datos.contenido, r.datos.nombre);
    else setError(t.importar.error);
  };

  const libre = !hayConexion || ocupado !== null;

  return (
    <div className="importar">
      <p className="ayuda-neutra">{t.importar.detalle}</p>
      <p className="ayuda-neutra">{minima ? t.importar.minima(limites.maxFilas, limites.alMes) : t.importar.avanzada(limites.maxFilas)}</p>
      {minima ? (
        <p className="ayuda-neutra">
          {t.importar.masEnFacturaPlus}{" "}
          <a className="enlace" href="/plan">
            {t.importar.verPlanes}
          </a>
        </p>
      ) : null}
      <p className="ayuda-neutra">{sinCupo ? t.importar.sinCupo(dia(limites.vuelveEl)) : t.importar.usadas(limites.usadasEsteMes, limites.alMes, dia(limites.vuelveEl))}</p>
      {aviso !== null ? <Aviso tono="exito">{aviso}</Aviso> : null}
      {error !== null ? <Aviso tono="error">{error}</Aviso> : null}
      {ocupado !== null ? (
        <div className="progreso" role="status">
          <span>{ocupado}</span>
          <progress />
        </div>
      ) : null}

      {paso.tipo === "elegir" ? (
        <section className="tarjeta" aria-label={t.importar.queImportar}>
          <div className="fila-campos">
            <Selector<TipoImportacion>
              id="importar-tipo"
              etiqueta={t.importar.queImportar}
              valor={tipo}
              opciones={limites.tipos.map((k) => ({ valor: k, texto: t.importar.tipos[k] }))}
              onChange={setTipo}
            />
          </div>
          <p className="ayuda-neutra">{t.importar.plantillaAyuda}</p>
          <div className="acciones">
            <Boton variante="secundario" onClick={() => void plantilla()} deshabilitado={!hayConexion}>
              {t.importar.plantilla}
            </Boton>
          </div>
          <div className="campo">
            <label htmlFor="importar-fichero">{t.importar.elegirFichero}</label>
            <input
              id="importar-fichero"
              ref={entrada}
              type="file"
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              disabled={libre || sinCupo || limites.tipos.length === 0}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f !== undefined) void alElegirFichero(f);
              }}
            />
          </div>
        </section>
      ) : null}

      {paso.tipo === "emparejar" ? (
        <section className="tarjeta" aria-label={t.importar.emparejarTitulo}>
          <h3>{t.importar.emparejarTitulo}</h3>
          <p className="ayuda-neutra">{t.importar.emparejarAyuda}</p>
          <p className="ayuda-neutra">{t.importar.filasLeidas(paso.fichero.hoja.filas.length)}</p>
          {paso.fichero.hoja.cabeceras.map((cabecera, i) => (
            <Selector<string>
              key={`${i}-${cabecera}`}
              id={`importar-columna-${i}`}
              etiqueta={cabecera === "" ? `${t.importar.columnaDelExcel} ${i + 1}` : cabecera}
              valor={paso.emparejamiento[i] ?? NO}
              opciones={[
                { valor: NO, texto: t.importar.noImportar },
                ...columnas.map((c) => ({ valor: c.key, texto: c.required ? `${c.header} *` : c.header })),
              ]}
              onChange={(v) => {
                const nuevo = paso.emparejamiento.map((x, j) => (j === i ? (v === NO ? null : v) : x === v ? null : x));
                setPaso({ ...paso, emparejamiento: nuevo });
              }}
            />
          ))}
          {faltan(paso.emparejamiento, columnas).length > 0 ? (
            <Aviso tono="aviso">{t.importar.faltanColumnas(faltan(paso.emparejamiento, columnas).map(nombreCampo).join(", "))}</Aviso>
          ) : null}
          <div className="acciones">
            <Boton onClick={() => void comprobar(paso.fichero, paso.emparejamiento)} deshabilitado={libre || faltan(paso.emparejamiento, columnas).length > 0}>
              {t.importar.comprobar}
            </Boton>
            <Boton variante="terciario" onClick={empezarDeNuevo}>
              {t.importar.cancelar}
            </Boton>
          </div>
        </section>
      ) : null}

      {paso.tipo === "revisar" ? <Revisar paso={paso} tipo={tipo} libre={libre} entendido={entendido} onEntendido={setEntendido} onImportar={() => void importar(paso)} onCancelar={empezarDeNuevo} /> : null}

      {paso.tipo === "hecho" ? (
        <section className="tarjeta" aria-label={t.importar.hechoTitulo}>
          <h3>{t.importar.hechoTitulo}</h3>
          <ul className="lista-filas">
            <li className="fila">{t.importar.hecho.creadas(paso.importacion.creadas)}</li>
            <li className="fila">{t.importar.hecho.duplicadas(paso.importacion.duplicadas)}</li>
            <li className="fila">{t.importar.hecho.rechazadas(paso.motivos.size)}</li>
          </ul>
          <div className="acciones">
            {paso.motivos.size > 0 ? (
              <Boton variante="secundario" onClick={() => descargarErrores(paso.fichero, paso.motivos)}>
                {t.importar.descargarErrores}
              </Boton>
            ) : null}
            <Boton onClick={empezarDeNuevo}>{t.importar.otra}</Boton>
          </div>
        </section>
      ) : null}

      <section className="tarjeta" aria-label={t.importar.historialTitulo}>
        <h3>{t.importar.historialTitulo}</h3>
        {historial.length === 0 ? <p className="ayuda-neutra">{t.importar.historialVacio}</p> : null}
        <ul className="lista-filas">
          {historial.map((h) => (
            <li key={h.id} className="fila">
              <span className="fila-texto">
                <span className="fila-titulo">{h.fichero}</span>
                <span>{t.importar.lineaHistorial(t.importar.tipos[h.tipo], fechaCorta(h.creadaEl), h.creadas)}</span>
                <span>{t.importar.estados[h.estado]}</span>
              </span>
              {limites.puedeDeshacer && h.estado !== "UNDONE" ? (
                confirmando === h.id ? (
                  <span className="fila-botones">
                    <Boton variante="peligro" onClick={() => void deshacer(h.id)} deshabilitado={libre}>
                      {t.importar.deshacer}
                    </Boton>
                    <Boton variante="terciario" onClick={() => setConfirmando(null)}>
                      {t.importar.cancelar}
                    </Boton>
                  </span>
                ) : (
                  <Boton variante="secundario" onClick={() => setConfirmando(h.id)} deshabilitado={libre}>
                    {t.importar.deshacer}
                  </Boton>
                )
              ) : null}
            </li>
          ))}
        </ul>
        {confirmando !== null ? <Aviso tono="aviso">{t.importar.confirmarDeshacer}</Aviso> : null}
      </section>
    </div>
  );
}

function Revisar({
  paso,
  tipo,
  libre,
  entendido,
  onEntendido,
  onImportar,
  onCancelar,
}: {
  readonly paso: PasoRevisar;
  readonly tipo: TipoImportacion;
  readonly libre: boolean;
  readonly entendido: boolean;
  readonly onEntendido: (v: boolean) => void;
  readonly onImportar: () => void;
  readonly onCancelar: () => void;
}) {
  const todos = [...paso.resultados.values()];
  const bien = todos.filter((r) => r.resultado === "OK").length;
  const duplicadas = todos.filter((r) => r.resultado === "DUPLICATE").length;
  const motivos = motivosPorFila(paso.grupos, paso.resultados);
  const errores = [...motivos.entries()].sort((a, b) => a[0] - b[0]);
  const facturas = tipo === "EXTERNAL_INVOICES";
  const sobran = errores.length - MAX_ERRORES_EN_PANTALLA;
  return (
    <section className="tarjeta" aria-label={t.importar.resumenTitulo}>
      <h3>{t.importar.resumenTitulo}</h3>
      <ul className="lista-filas">
        <li className="fila">{t.importar.resumen.bien(bien, facturas)}</li>
        {duplicadas > 0 ? <li className="fila">{t.importar.resumen.duplicadas(duplicadas)}</li> : null}
        {errores.length > 0 ? <li className="fila">{t.importar.resumen.errores(errores.length)}</li> : null}
      </ul>
      {paso.libres !== null && paso.libres < bien ? <Aviso tono="aviso">{t.importar.resumen.noCaben(paso.libres)}</Aviso> : null}
      {errores.length > 0 ? (
        <>
          <h4>{t.importar.erroresTitulo}</h4>
          <ul className="lista-filas">
            {errores.slice(0, MAX_ERRORES_EN_PANTALLA).map(([fila, texto]) => (
              <li key={fila} className="fila">
                <span className="fila-texto">
                  <span className="fila-titulo">{t.importar.fila(fila)}</span>
                  <span>{texto}</span>
                </span>
              </li>
            ))}
          </ul>
          {sobran >= 1 ? (
            <p className="ayuda-neutra">{t.importar.masErrores(sobran)}</p>
          ) : null}
          <div className="acciones">
            <Boton variante="secundario" onClick={() => descargarErrores(paso.fichero, motivos)}>
              {t.importar.descargarErrores}
            </Boton>
          </div>
        </>
      ) : null}
      {facturas ? (
        <>
          <Aviso tono="info">{t.importar.avisoFacturas}</Aviso>
          <Casilla id="importar-entendido" etiqueta={t.importar.entendido} marcada={entendido} onChange={onEntendido} />
        </>
      ) : null}
      <div className="acciones">
        <Boton onClick={onImportar} deshabilitado={libre || bien === 0 || (facturas && !entendido)}>
          {t.importar.importar(bien, facturas)}
        </Boton>
        <Boton variante="terciario" onClick={onCancelar}>
          {t.importar.cancelar}
        </Boton>
      </div>
    </section>
  );
}
